import { createSelector } from 'reselect';
import { combineReducers } from 'redux';
import { array, option } from 'fp-ts';
import { get, includes, compose, isEqual, map, values } from 'lodash/fp';
import { flow } from 'fp-ts/function';
import { getProp } from '@mews/fp';
import { createAsyncActionTypes } from '@mews/framework';
import {
    GeneralEnterpriseSubscriptionTier,
    IntegrationData,
    IntegrationDataModes,
    IntegrationDataScope,
    IntegrationType,
} from '@mews-commander/data-access';
import { ApplicationState, CommanderThunkAction, AsyncAction } from '@mews-commander/types';
import { createFetchAction } from '@mews-commander/utils';
import moduleSelector from '../selector';
import prefix from '../prefix';
import { setApplicationRegions } from './applicationRegions';
import { setApplicationPermissions } from './applicationPermissions';
import {
    createEntitiesReducer,
    createBaseEntitySelectors,
    createEntityMetaReducer,
    createAsyncMetaStateSelectors,
    Entities,
    FetchSuccessEntitiesPayload,
    MetaState,
} from './utils';

interface IntegrationDataModuleState extends MetaState {
    data: Entities<CustomIntegrationData>;
}

export const NAME = 'integrationData';

export const INTEGRATION_DATA_FETCH = prefix('INTEGRATION_DATA_FETCH');
export const [INTEGRATION_DATA_FETCH_STARTED, INTEGRATION_DATA_FETCH_SUCCEEDED, INTEGRATION_DATA_FETCH_FAILED] =
    createAsyncActionTypes(INTEGRATION_DATA_FETCH);

const dataReducer = createEntitiesReducer({
    FETCH_SUCCESS: INTEGRATION_DATA_FETCH_SUCCEEDED,
});
const metaReducer = createEntityMetaReducer({
    STARTED: INTEGRATION_DATA_FETCH_STARTED,
    SUCCEEDED: INTEGRATION_DATA_FETCH_SUCCEEDED,
    FAILED: INTEGRATION_DATA_FETCH_FAILED,
});

export const integrationDataReducer = combineReducers({
    data: dataReducer,
    meta: metaReducer,
});

export interface CustomIntegrationData extends Omit<IntegrationData, 'id'> {
    id: string;
}

type FetchIntegrationData = FetchSuccessEntitiesPayload<CustomIntegrationData>;

const getAllExtent = { applicationRegions: true, permissions: true };

export const fetchIntegrationData =
    (enterpriseId: string): CommanderThunkAction<AsyncAction<FetchIntegrationData>> =>
    async (dispatch, _getState, { repositories }) => {
        const promise = repositories.integrationData
            .getAll({ enterpriseId, extent: getAllExtent })
            .then(({ integrationData, applicationRegions, permissions }) => {
                if (applicationRegions && array.isNonEmpty(applicationRegions)) {
                    dispatch(setApplicationRegions(applicationRegions));
                }
                if (permissions && array.isNonEmpty(permissions)) dispatch(setApplicationPermissions(permissions));

                return {
                    entities: map((value) => ({ ...value, id: value.identifier }), integrationData),
                };
            });

        return dispatch(createFetchAction(INTEGRATION_DATA_FETCH, promise));
    };

const getIntegrationDataState = compose<ApplicationState, IntegrationDataModuleState, unknown>(
    get(NAME),
    moduleSelector
);

export const [isIntegrationDataFetching, hasIntegrationDataFailed] =
    createAsyncMetaStateSelectors(getIntegrationDataState);

export const [
    getIntegrationDataEntities,
    getIntegrationDataByIds,
    getIntegrationDataByOwnerId,
    getSingleIntegrationData,
] = createBaseEntitySelectors(getIntegrationDataState, 'data');

export const getIntegrationData = createSelector<
    ApplicationState,
    Entities<CustomIntegrationData>,
    Array<CustomIntegrationData>
>(getIntegrationDataEntities, values);

const getModes = get('modes');

export const hasConnectableMode = compose(includes(IntegrationDataModes.Connectable), getModes);
export const hasPurchasableMode = compose(includes(IntegrationDataModes.Purchasable), getModes);

export const hasSingleIntegrationConnectableMode = compose(hasConnectableMode, getSingleIntegrationData);
export const hasSingleIntegrationPurchasableMode = compose(hasPurchasableMode, getSingleIntegrationData);

export const getSingleIntegrationCancelationPeriod = compose(get('cancelationPeriod'), getSingleIntegrationData);
export const getSingleIntegrationClassification = compose(get('classification'), getSingleIntegrationData);
export const getSingleIntegrationCompanyName = compose(get('companyName'), getSingleIntegrationData);
export const getSingleIntegrationDescription = compose(get('description'), getSingleIntegrationData);
export const getSingleIntegrationImageIds = compose(get('imageIds'), getSingleIntegrationData);
export const getSingleIntegrationIntegrationType = compose(get('integrationType'), getSingleIntegrationData);
export const getSingleIntegrationKind = compose(get('kind'), getSingleIntegrationData);
export const getSingleIntegrationLogoImageId = compose(get('logoImageId'), getSingleIntegrationData);
export const getSingleIntegrationLogoImageName = compose(get('logoImageName'), getSingleIntegrationData);
export const getSingleIntegrationMarkdownDescription = compose(get('markdownDescription'), getSingleIntegrationData);
export const getSingleIntegrationName = compose(get('name'), getSingleIntegrationData);
export const getSingleIntegrationPricing = compose(get('pricing'), getSingleIntegrationData);
export const getSingleIntegrationScope = compose(get('scope'), getSingleIntegrationData);
export const getSingleIntegrationSetupInstructions = compose(get('setupInstructions'), getSingleIntegrationData);
export const getSingleIntegrationShortDescription = compose(get('shortDescription'), getSingleIntegrationData);
export const getSingleIntegrationSupportEmail = compose(get('supportEmail'), getSingleIntegrationData);
export const getSingleIntegrationTermsAndConditions = compose(get('termsAndConditions'), getSingleIntegrationData);
export const getSingleIntegrationTier = compose(get('tier'), getSingleIntegrationData);
export const getSingleIntegrationTrialPeriod = compose(get('trialPeriod'), getSingleIntegrationData);
export const getSingleIntegrationType = compose(get('type'), getSingleIntegrationData);
export const getSingleIntegrationVideoUrl = compose(get('videoUrl'), getSingleIntegrationData);
export const getSingleIntegrationWebsiteUrl = compose(get('websiteUrl'), getSingleIntegrationData);

export const getSingleIntegrationPricingCurrencyCode = compose(get('currencyCode'), getSingleIntegrationPricing);

const getSingleIntegrationSubscriptionTierPricings = compose(
    get('subscriptionTierPricings'),
    getSingleIntegrationPricing
);

export const getSingleIntegrationSubscriptionTierPricing = createSelector(
    getProp<GeneralEnterpriseSubscriptionTier>()('subscriptionTier'),
    getSingleIntegrationSubscriptionTierPricings,
    (subscriptionTier, subscriptionTierPricings) =>
        subscriptionTierPricings?.find(
            // @ts-expect-error GeneralEnterpriseSubscriptionTier and EnterpriseSubscriptionTier comparison
            (subscriptionTierPricing) => subscriptionTierPricing.subscriptionTier === subscriptionTier
        )
);

export const isIntegrationServiceScoped = compose(isEqual(IntegrationDataScope.Service), getSingleIntegrationScope);
export const isIntegrationEnterpriseScoped = compose(
    isEqual(IntegrationDataScope.Enterprise),
    getSingleIntegrationScope
);

export const isSingleIntegrationAddOn = compose(isEqual(IntegrationType.AddOn), getSingleIntegrationIntegrationType);

export const isSingleIntegrationService = compose(
    isEqual(IntegrationType.Service),
    getSingleIntegrationIntegrationType
);

export const isSingleIntegrationWithoutPricing = flow(getSingleIntegrationPricing, option.fromNullable, option.isNone);
